import { useSelector } from 'react-redux';
import { useFlag } from '@unleash/proxy-client-react';
import { portalLevelPermissions } from '../../../../../submodules/web-shared/modules/Permissions/PortalLevelPermissions';
import PERMISSIONS_LIST from '../../../../../submodules/web-shared/modules/Permissions/permissions-list';
import { useUserCompanies } from '../../../../../submodules/web-shared/reducers/user-companies/hooks';
import { USER_ROLE_KIND } from '../../../../../submodules/web-shared/types/User.types';
import { COMPANY_SETTING_NAME } from '../../../../global/modules/companies/api/company-settings/enums/company-settings';
import { SUVReduxState } from '../../../reducers/type';

const useRiskMonitorAccess = () => {
  const currentUserPhoneGroups = useSelector((state: SUVReduxState) => state.auth.user?.in_app_call_groups);
  const { isCompanySettingEnabled, current } = useUserCompanies();
  const isShipmentHardResetEnabled = useFlag('CORE-OK-63144-shipment-hard-reprocess');

  const hasPermission = (permission: PERMISSIONS_LIST, portalId?: number) => {
    return portalLevelPermissions.isPermissionEnabled(permission, portalId);
  };

  const hasRMAccess = () => {
    return isCompanySettingEnabled(COMPANY_SETTING_NAME.RISK_MONITOR_ACCESS)
      && hasPermission(PERMISSIONS_LIST.sentinel_access);
  };

  const hasRMManageManualIncidentsAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.manage_manual_incident, portalId);
  };

  const hasRmCreateManualEventsAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.create_manual_events, portalId);
  };

  const hasRMReminderActionAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.reminder_action, portalId);
  };

  const hasRMViewQrcAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.view_qrcs, portalId);
  };

  const hasRMContactDepartmentFilteringAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.contact_department_filtering, portalId);
  };

  const hasRMRiskGPTInvestigationAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.risk_gpt_investigation, portalId);
  };

  const hasRMSendEscalationEmailsAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.send_escalation_emails, portalId);
  };

  const hasRMRiskGPTChatTabAccess = (portalId: number) => {
    return hasRMAccess()
      && hasPermission(PERMISSIONS_LIST.risk_gpt_chat_tab, portalId);
  };

  const hasViolationApproveAccess = (portalId: number) => {
    return hasRMAccess() && hasPermission(PERMISSIONS_LIST.violation_approve, portalId);
  };

  const hasInAppCallingAccess = () => {
    return hasRMAccess() && hasPermission(PERMISSIONS_LIST.in_app_calling) &&
      !!currentUserPhoneGroups?.length;
  };

  const hasInAppTextingAccess = () => {
    return hasRMAccess() && hasPermission(PERMISSIONS_LIST.in_app_texting);
  };

  const hasRMBadActorOverlayAccess = () => {
    return hasRMAccess() && current?.role_kind === USER_ROLE_KIND.OH;
  };

  const hasHardResetShipmentAccess = (portalId: number) => {
    return isShipmentHardResetEnabled && hasRMAccess() && hasPermission(PERMISSIONS_LIST.shipments_hard_reset, portalId);
  };

  return {
    hasRMAccess,
    hasRMManageManualIncidentsAccess,
    hasRmCreateManualEventsAccess,
    hasRMReminderActionAccess,
    hasRMViewQrcAccess,
    hasRMContactDepartmentFilteringAccess,
    hasRMRiskGPTInvestigationAccess,
    hasRMSendEscalationEmailsAccess,
    hasRMRiskGPTChatTabAccess,
    hasViolationApproveAccess,
    hasInAppCallingAccess,
    hasInAppTextingAccess,
    hasRMBadActorOverlayAccess,
    hasHardResetShipmentAccess,
  };
};

export default useRiskMonitorAccess;